import moment from "moment";
import React, {useEffect, useRef, useState} from "react";
import SalesAccount from "./books/SalesAccount";
import WarehousesAccount from "./books/WarehousesAccount";
import ExpensesAccount from "./books/ExpensesAccount";
import TaxAccount from "./books/TaxAccount";
import PayrollAccount from "./books/PayrollAccount";
import CashBookAccount from "./books/CashBookAccount";
import BankBookAccount from "./books/BankBookAccount";
import { UserModel } from "../../modules/auth/models/UserModel";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "../../../setup";
import { downloadReport, exportAccountBooks, exportPayments, exportSalesBooks, exportTaxBooks, exportWarehousesBooks, getCurReport } from "./AccountBooksCRUD";
import { download, Toast } from "../../modules/common/helper";
import { AccountBookReportModel } from "../../models/account-books/AccountBooksReport";
import IndexAccount from "./books/IndexAccount";
import { usePermissionChecker } from "../../hooks/PermissionChecker";
import { BUSINESS_TYPE_HOUSE_HOLD_1, LIST_PERMISSIONS } from "../../constants/common";

const AccountBooks = () => {
    const initYear = moment().format('YYYY')
    const fp = useRef<any>(null);
    const [curTab, setCurTab] = useState<string>('index');
    const [curReport, setCurReport] = useState<AccountBookReportModel>()
    const [year, setYear] = useState<any>(initYear);
    const [timeRange, setTimeRange] = useState<{opening: string, closing: string}>();
    const user: UserModel = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as UserModel
    const openConfig = user?.store?.open_store_config;
    const {hasPermission} = usePermissionChecker();
    const [businessType, setBussinessType] = useState<number>(BUSINESS_TYPE_HOUSE_HOLD_1);
    const getYearList = () => {
        let storeStartTime = user?.store?.created_at;
        if (!storeStartTime) return [];
        const currentYear = moment().year();
        const startYear = currentYear - 5;
        const years = [];
        for (let year = currentYear; year >= startYear; year--) {
            years.push(year.toString());
        }
        return years;
    }


    useEffect(() => {
		document.title = 'Sento - Sổ sách báo cáo';
	})
    const [isFullYear, setIsFullYear] = useState<boolean>(false);

    const setTime = (time: number, type: string) => {
        switch (type) {
            case 'month':
                let month = moment({ year: parseInt(year), month: time });
                setTimeRange({
                    opening: month.startOf('month').format('YYYY-MM-DD'), 
                    closing: month.endOf('month').format('YYYY-MM-DD')
                });
                break;
            case 'quarter':
                if (time === 0) {
                    let yearMonent = moment().year(parseInt(year));
                    setTimeRange({
                        opening: yearMonent.startOf('year').format('YYYY-MM-DD'), 
                        closing: yearMonent.endOf('year').format('YYYY-MM-DD')
                    });
                    setIsFullYear(true)
                    break;
                }
                let quarter = moment().year(parseInt(year)).quarter(time);
                setTimeRange({
                    opening: quarter.startOf('quarter').format('YYYY-MM-DD'),
                    closing: quarter.endOf('quarter').format('YYYY-MM-DD')
                });
                setIsFullYear(false)
                break
            case 'year':
                let yearMonent = moment().year(time);
                setTimeRange({
                    opening: yearMonent.startOf('year').format('YYYY-MM-DD'), 
                    closing: yearMonent.endOf('year').format('YYYY-MM-DD')
                });
                setYear(time);
                break;
        }
    }

    useEffect(() => {
        setInitTimeRange();
        checkBusinessType();
    }, [])

    const checkBusinessType = () => {
        const storeInfo = user.store;
        setBussinessType(storeInfo.is_company ?? BUSINESS_TYPE_HOUSE_HOLD_1);
    };

    useEffect(() => {
        if (timeRange?.opening && timeRange?.closing) {
            getReport(timeRange.opening, timeRange.closing);
        }
    }, [timeRange])

    const getReport = async (opening: string, closing: string) => {
        try {
            const data = await getCurReport({opening , closing})
            setCurReport(data?.data?.data?.report)
        } catch (e: any) {
            console.log('Không thể lấy báo cáo');
        }
    }
    const setInitTimeRange = () => {
        let startDate = moment().startOf('year').format('YYYY-MM-DD')
        let endDate = moment().endOf('year').format('YYYY-MM-DD')
        if (openConfig?.report_period === 'monthly') {
            startDate = moment().startOf('month').format('YYYY-MM-DD')
            endDate = moment().endOf('month').format('YYYY-MM-DD')
        }
        if (openConfig?.report_period === 'quarterly') {
            startDate = moment().startOf('quarter').format('YYYY-MM-DD')
            endDate = moment().endOf('quarter').format('YYYY-MM-DD')
        }
        setTimeRange({
            opening: startDate,
            closing: endDate
        });
    }

    const handleExport = async () => {
        await exportAccountBooks({ start_date: timeRange?.opening, end_date: timeRange?.closing });
        if (timeRange) {
            getReport(timeRange?.opening, timeRange?.closing);
        }
    }

    const handleExportSingle = async (template: string) => {
        const params = {
            start_date: timeRange?.opening,
            end_date: timeRange?.closing,
            template
        };
        const mapping: Record<string, () => Promise<any>> = {
            s1a: () => exportSalesBooks(params),
            s2a: () => exportSalesBooks(params),
            s2b: () => exportSalesBooks(params),
            s2c: () => exportSalesBooks(params),
            s2d: () => exportWarehousesBooks(params),
            s2e: () => exportPayments(params),
            s3a: () => exportTaxBooks(params),
        };
        const response = await mapping[template]?.();

        if (response?.data) {
            download(response.data, template + '-HKD');
        }
        
    }

    const accountBooksMap: Record<string, React.ReactNode> = {
        'index': <IndexAccount businessType={businessType} changeTab={setCurTab} handleExport={handleExportSingle} />,
        'sales': <SalesAccount isFullYear={isFullYear} changeTab={setCurTab} opening={timeRange?.opening} closing={timeRange?.closing}/>,
        'warehouses': <WarehousesAccount isFullYear={isFullYear} changeTab={setCurTab} opening={timeRange?.opening} closing={timeRange?.closing}/>,
        'expenses': <ExpensesAccount isFullYear={isFullYear} changeTab={setCurTab} opening={timeRange?.opening} closing={timeRange?.closing}/>,
        'taxbook': <TaxAccount year={year} isFullYear={isFullYear} changeTab={setCurTab} opening={moment().year(parseInt(year)).startOf('year').format('YYYY-MM-DD')} closing={moment().year(parseInt(year)).endOf('year').format('YYYY-MM-DD')}/>,
        'payroll': <PayrollAccount
            isFullYear={isFullYear}
            changeTab={setCurTab}  
            year={moment(timeRange?.opening, 'YYYY-MM-DD').format('YYYY')}
            opening={timeRange?.opening} closing={timeRange?.closing}
        />,
        'cashbook': <CashBookAccount isFullYear={isFullYear} changeTab={setCurTab} opening={timeRange?.opening} closing={timeRange?.closing}/>,
        'bankbook': <BankBookAccount isFullYear={isFullYear} changeTab={setCurTab} opening={timeRange?.opening} closing={timeRange?.closing}/>,
    };

    const handleDownload = async (curReport: AccountBookReportModel) => {
        try {
            const { data } = await downloadReport({report_id: curReport.id});
            download(data, curReport.file_name);
        } catch (e: any) {
            Toast.fire({
                icon: 'error',
                title: e?.response?.data?.messag ?? 'Không thể tải file'
            })
        }
        
    }

    return <>
        <div className="card">
            <div className="card-header py-4">
                <div className="col-12">
                    <div className="row">
                        <div className='col-6 col-md-auto'>
                            <select
                                className="form-select"
                                value={moment(timeRange?.opening, 'YYYY-MM-DD').year()}
                                onChange={(e) => setTime(parseInt(e.target.value), 'year')}
                                name="status"
                            >
                                {getYearList().map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                        {openConfig?.report_period === 'monthly' && (
                            <div className='col-6 col-md-auto '>
                                <select
                                    className="form-select"
                                    value={moment(timeRange?.opening, 'YYYY-MM-DD').month()}
                                    onChange={(e) => setTime(parseInt(e.target.value), 'month')}
                                    name="month"
                                >
                                    <option value={'0'}>Tháng 1</option>
                                    <option value={'1'}>Tháng 2</option>
                                    <option value={'2'}>Tháng 3</option>
                                    <option value={'3'}>Tháng 4</option>
                                    <option value={'4'}>Tháng 5</option>
                                    <option value={'5'}>Tháng 6</option>
                                    <option value={'6'}>Tháng 7</option>
                                    <option value={'7'}>Tháng 8</option>
                                    <option value={'8'}>Tháng 9</option>
                                    <option value={'9'}>Tháng 10</option>
                                    <option value={'10'}>Tháng 11</option>
                                    <option value={'11'}>Tháng 12</option>
                                </select>
                            </div>
                        )}
                        {openConfig?.report_period === 'quarterly' && (
                            <div className='col-6 col-md-auto '>
                                <select
                                    className="form-select"
                                    value={isFullYear ? 0 : moment(timeRange?.opening, 'YYYY-MM-DD').quarter()}
                                    onChange={(e) => setTime(parseInt(e.target.value), 'quarter')}
                                    name="quarterly"
                                >
                                    <option value={'1'}>Quý 1</option>
                                    <option value={'2'}>Quý 2</option>
                                    <option value={'3'}>Quý 3</option>
                                    <option value={'4'}>Quý 4</option>
                                    <option value={'0'}>Cả năm</option>
                                </select>
                            </div>
                        )}
                        {(hasPermission(LIST_PERMISSIONS.ACCOUNT_BOOKS)
                        ) && (
                            <div className="col-auto ms-auto">
                                {curReport?.file_path && (
                                    <button
                                        className={"col-auto btn btn-sm me-2 mt-3 btn-success"}
                                        onClick={() => handleDownload(curReport)}
                                    >
                                        Tải Báo Cáo
                                    </button>
                                )}
                                {!isFullYear && (
                                    <button
                                        className={"col-auto btn btn-sm me-2 mt-3 btn-warning"}
                                        type="button"
                                        onClick={handleExport}
                                    >
                                        Chốt sổ
                                    </button>
                                )}
                            </div>
                        )}
                        
                    </div>
                </div>
            </div>
        </div>
        {accountBooksMap[curTab]}
    </>
}
export default AccountBooks;